import { useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCcw } from "lucide-react";

/**
 * Ödeme başarısız / iptal sayfası.
 *
 * Backend kaydını sorgular; eğer server-side callback aslında "paid"
 * demişse (nadir de olsa tarayıcı fail sayfasına düşebilir), doğru
 * bilgiyi gösterip başarılı sayfasına yönlendirir.  Aksi halde retry
 * seçeneği gösterir.
 */
export default function PaymentFail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const oid =
    params.get("oid") ||
    params.get("merchant_oid") ||
    (typeof window !== "undefined"
      ? sessionStorage.getItem("penz_last_paytr_oid")
      : null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!oid) return;
      try {
        const { data } = await api.get(`/paytr/payment/${encodeURIComponent(oid)}`);
        if (cancelled) return;
        // Nadir de olsa server-side "paid" gelmişse → başarı sayfasına gönder
        if (data.status === "paid") {
          navigate(`/odeme/basarili?oid=${encodeURIComponent(oid)}`, { replace: true });
        }
      } catch {
        // sessizce yut — sayfayı yine de fail olarak göster
      }
    })();
    return () => { cancelled = true; };
  }, [oid, navigate]);

  return (
    <div className="space-y-6" data-testid="payment-fail-page">
      <StickyPageHead>
        <PageHeader
          title="Ödeme Başarısız"
          subtitle="Ödemeniz tamamlanamadı"
        />
      </StickyPageHead>

      <div className="bg-white border border-slate-200 rounded-xl p-8 max-w-xl mx-auto text-center">
        <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto">
          <AlertCircle className="w-8 h-8 text-rose-600" />
        </div>
        <div className="font-display text-xl font-semibold text-slate-900 mt-4">
          Ödeme tamamlanamadı
        </div>
        <p className="text-sm text-slate-600 mt-2">
          İşleminiz PayTR tarafında başarısız oldu veya iptal edildi.
          Aboneliğiniz aktif edilmedi ve kartınızdan herhangi bir tahsilat
          yapılmadı.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          <Button
            onClick={() => navigate("/abonelik")}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="payment-fail-retry-button"
          >
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Yeniden Dene
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/abonelik")}
            data-testid="payment-fail-back-button"
          >
            Planlara Dön
          </Button>
        </div>

        <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-400">
          Sorun mu var? <Link to="/support" className="text-slate-600 underline hover:text-slate-900">Destek ekibimize ulaşın</Link>.
        </div>
      </div>
    </div>
  );
}
